import React from 'react';
import { motion } from 'framer-motion';
import { 
  HomeIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const RentalProfitsSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Continuous Rental Profits
          </motion.h2>
          
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            When your card is invested in rental properties, you receive all rental income 
            for the entire project period. Our business model ensures continuous 
            passive income generation and long-term wealth building for every card holder.
          </motion.p>
        </motion.div>

        {/* Key Benefits Grid */}
        <motion.div
          className="grid md:grid-cols-4 gap-8 mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="text-center p-8 bg-gradient-to-b from-purple-600/20 to-purple-800/20 backdrop-blur-lg rounded-3xl border border-purple-500/30"
            whileHover={{ scale: 1.05, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <HomeIcon className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-3">Rental Properties</h4>
            <p className="text-gray-300">Primary focus on high-yield European rental real estate</p>
          </motion.div>

          <motion.div 
            className="text-center p-8 bg-gradient-to-b from-yellow-600/20 to-yellow-800/20 backdrop-blur-lg rounded-3xl border border-yellow-500/30"
            whileHover={{ scale: 1.05, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <CalendarDaysIcon className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-3">8-Year Period</h4>
            <p className="text-gray-300">Complete project and profit sharing duration</p>
          </motion.div>

          <motion.div 
            className="text-center p-8 bg-gradient-to-b from-cyan-600/20 to-cyan-800/20 backdrop-blur-lg rounded-3xl border border-cyan-500/30"
            whileHover={{ scale: 1.05, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <CurrencyEuroIcon className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-3">Monthly Income</h4>
            <p className="text-gray-300">Receive your share of rental income every month</p>
          </motion.div>

          <motion.div 
            className="text-center p-8 bg-gradient-to-b from-green-600/20 to-green-800/20 backdrop-blur-lg rounded-3xl border border-green-500/30"
            whileHover={{ scale: 1.05, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <ArrowTrendingUpIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h4 className="text-xl font-bold text-white mb-3">Compounding</h4>
            <p className="text-gray-300">96 months of returns can exceed initial investment by 5-10x</p>
          </motion.div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.button
            onClick={() => document.getElementById('minting').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-10 py-4 rounded-2xl text-xl font-semibold transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your 8-Year Income Journey
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default RentalProfitsSection;